import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, Loader2, Pencil, PartyPopper } from "lucide-react";
import Confetti from "@/pages/payment/Confetti";

const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

/** Public tip-goal progress bar + owner-only goal editor. */
export default function TipGoal({ coin, totalSol }) {
  const [goal, setGoal] = useState(Number(coin.tip_goal_sol) || 0);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(coin.tip_goal_sol || "");
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const smashed = goal > 0 && totalSol >= goal;

  useEffect(() => {
    if (!smashed) return;
    const key = `goal_party_${coin.slug}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    setCelebrate(true);
    const t = setTimeout(() => setCelebrate(false), 6000);
    return () => clearTimeout(t);
  }, [smashed, coin.slug]);

  const save = async () => {
    const g = Number(String(val).replace(/[^0-9.]/g, "")) || 0;
    setBusy(true);
    try {
      await api.patch(`/coins/${coin.slug}/tip-goal`, { goal_sol: g });
      setGoal(g);
      setEditing(false);
      toast.success(g > 0 ? `Goal set: ${fmt(g)} SOL` : "Goal cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save goal");
    } finally {
      setBusy(false);
    }
  };

  if (!goal && !coin.is_owner) return null;

  const pct = goal > 0 ? Math.min(100, (totalSol / goal) * 100) : 0;

  return (
    <div className="border border-[#3a3a3a] bg-[#050505] p-4 mb-4" data-testid="tip-goal">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#cfff04] flex items-center gap-1.5">
          <Target size={11} /> Tip goal
        </span>
        {coin.is_owner && !editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid="tip-goal-edit"
            className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] hover:text-[#cfff04] flex items-center gap-1"
          >
            <Pencil size={9} /> {goal > 0 ? "Edit" : "Set a goal"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex items-center gap-2" data-testid="tip-goal-editor">
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 10"
            inputMode="decimal"
            data-testid="tip-goal-input"
            className="w-28 bg-[#121212] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none px-3 py-2 font-mono text-sm"
          />
          <span className="font-mono text-[11px] uppercase text-[#d0d0d0]">SOL</span>
          <button
            onClick={save}
            disabled={busy}
            data-testid="tip-goal-save"
            className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 size={10} className="animate-spin" /> : null} Save
          </button>
          <button
            onClick={() => setEditing(false)}
            data-testid="tip-goal-cancel"
            className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] hover:text-white"
          >
            Cancel
          </button>
        </div>
      ) : goal > 0 ? (
        <>
          {celebrate && <Confetti />}
          <div className="h-3 bg-[#121212] border border-[#3a3a3a] overflow-hidden" data-testid="tip-goal-bar">
            <div
              className="h-full bg-gradient-to-r from-[#cfff04] to-[#00ff41] transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 font-mono text-[11px] uppercase tracking-wider">
            <span className="text-[#e2e2e2]" data-testid="tip-goal-progress">
              {fmt(totalSol)} / {fmt(goal)} SOL
            </span>
            <span className={pct >= 100 ? "text-[#00ff41]" : "text-[#d0d0d0]"}>
              {pct >= 100 ? "Goal smashed!" : `${pct.toFixed(0)}%`}
            </span>
          </div>
          {smashed && (
            <div className="mt-3 border border-[#00ff41] bg-[#00ff41]/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="goal-smashed">
              <span className="font-display font-bold text-sm text-[#00ff41] flex items-center gap-2">
                <PartyPopper size={15} /> {fmt(goal)} SOL goal smashed!
              </span>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  `We just smashed our ${fmt(goal)} SOL tip goal for ${coin.name} ($${coin.ticker}) on @solspurr 🎉 Come see what the hype is about 👇`
                )}&url=${encodeURIComponent(`${window.location.origin}/c/${coin.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="goal-smashed-share"
                className="btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2"
              >
                Share the moment →
              </a>
            </div>
          )}
        </>
      ) : (
        <p className="font-mono text-[11px] text-[#d0d0d0]">
          Set a public SOL goal — fans love filling a bar.
        </p>
      )}
    </div>
  );
}
